import { SectionContainer } from '@/pages/shared-components/SectionContainer'
import { MaintainPlatformAdminsV2 } from '../Forms/MaintainPlatformAdminsV2'
import { useCurrentOrganisation } from '../useCurrentOrganisation'
import { useRestClient } from '@/contexts/QueryProvider'
import { useSessionStorage } from '@/hooks'
import { isPlatformAdmin, isSuperUser } from '@/util'
import { Button } from '@/components/shadcn/button'
import { IconEditOutline } from '@icons/IconEditOutline'
import { Avatar, AvatarFallback } from '@/components/shadcn/avatar'
import { getInitials } from '@/lib/utils'
import clsx from 'clsx'
import { Suspense } from 'react'
import { LoadingComponent } from '@/components/ui/LoadingComponent'
import { QueryErrorBoundary } from '@/catalog-apps/components/QueryErrorBoundary'

export function OrgPlatformAdminsSection() {
  return (
    <QueryErrorBoundary errorMessage="Failed to fetch platform admins">
      <Suspense fallback={<LoadingComponent />}>
        <SectionContent />
      </Suspense>
    </QueryErrorBoundary>
  )
}

function SectionContent() {
  const [userRole] = useSessionStorage('roles', 1)
  const [sub] = useSessionStorage('sub', '')
  const organisation = useCurrentOrganisation()
  const { id } = organisation

  const { getSubscriptionsApiListOrganisationPlatformAdmins } = useRestClient()
  const { data: currentAdmins } = getSubscriptionsApiListOrganisationPlatformAdmins.useSuspenseQuery({
    query: { organisationId: id },
  })

  const canEditAdmins =
    isSuperUser(userRole) || (isPlatformAdmin(userRole) && currentAdmins?.some((x) => x.id.toString() == sub))

  return (
    <SectionContainer
      title="Platform Admins"
      action={
        <MaintainPlatformAdminsV2
          sheetTitle={`${organisation?.name} > Manage platform admins`}
          sheetDescription="Manage the platform admins of the organisation."
          sheetTriggerTooltip="Manage platform admins"
          sheetTrigger={
            <Button disabled={!canEditAdmins} variant="ghost" size="icon">
              <IconEditOutline />
            </Button>
          }
        />
      }
    >
      <div className="flex flex-row flex-wrap gap-2 whitespace-nowrap text-base">
        {!currentAdmins.length && <span className="font-light italic">...No administrators assigned...</span>}
        {currentAdmins.map((a, i) => (
          <div className="flex items-center gap-x-1" key={a.id}>
            <Avatar className="size-8 ring-2 ring-background">
              <AvatarFallback className="bg-accent text-sm text-accent-foreground">
                {getInitials(clsx(a.givenName, a.familyName))}
              </AvatarFallback>
            </Avatar>
            <span className="font-medium">{`${a.givenName} ${a.familyName}${i < currentAdmins.length - 1 ? ',' : ''}`}</span>
          </div>
        ))}
      </div>
    </SectionContainer>
  )
}
